/**
 * @component ry-tile-checkbox
 * @category  form
 * @summary   Large card-style checkbox (Archetype A: stateful binary toggle). Wraps a visually-hidden `<input type="checkbox">` with content slots for icon, heading, meta, and optional badge/pill. Independent toggling per tile (NOT a radio group — use `<ry-tile-radio>` for single-select). ry-ui-only — no Fomantic equivalent.
 *
 * @attribute name       string?              Form field name. Multiple tiles with the same name behave as independent checkboxes (each toggles freely) — typical for `name="features[]"` patterns.
 * @attribute value      string?   default="1"   Submitted value when checked. Default `"1"` matches the HTML checkbox default.
 * @attribute checked    boolean   default=false   Initial checked state.
 * @attribute disabled   boolean   default=false   Non-interactive. Pure attribute hook on the inner input.
 * @attribute icon       string?              Icon name from the registry. Renders into the icon slot.
 * @attribute heading    string?              Bold title text.
 * @attribute meta       string?              Smaller description text.
 * @attribute badge      string?              Right-side badge text (e.g. "Included").
 * @attribute pill       'good' | 'bad' ?     Inline pill style next to the heading. Combine with [pill-text] to render.
 * @attribute pill-text  string?              Pill label.
 * @attribute warn       boolean   default=false   Amber-tinted visual variant. Pure CSS hook.
 *
 * @property  checked    boolean   Live checked state (mirrors the inner input).
 * @property  value      string    Read-only — the inner input's value attribute.
 *
 * @event ry:beforechange
 *        detail: { value: string, checked: boolean, previous: boolean }
 *        fires:  from the inner input's `click` (tile click, Space) before the toggle commits. `checked` is what it is ABOUT to become, `previous` what it is now.
 *        cancelable: preventDefault() cancels the inner input's click — the platform leaves `checked` untouched and `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: string, checked: boolean }
 *        fires:  on user toggle (click or Space on the hidden input). detail.checked is the new state.
 *
 * @keyboard  Tab moves focus to the inner input; Space toggles checked state.
 *
 * @state     `[checked]` attribute mirrors the inner input; updated on every ry:change.
 *
 * @content   None — `<ry-tile-checkbox>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-tile-checkbox[name?][value?][checked?][disabled?]
 *     > input.ry-tile__input  type="checkbox"  (visually hidden)
 *     > span.ry-tile           [data-ry-target="tile"]
 *       > span.ry-tile__icon       (only when [icon])
 *       > span.ry-tile__heading    (only when [heading])    {heading} <pill>
 *       > span.ry-tile__meta       (only when [meta])       {meta}
 *       > span.ry-tile__badge      (only when [badge])      {badge}
 *
 * @gotcha  This is a CHECKBOX, not a radio. Sharing `[name]` does NOT cause auto-uncheck — each tile toggles independently. Use `<ry-tile-radio>` for single-selection groups.
 *
 * @gotcha  Default `[value]` is `"1"` (HTML checkbox default). When checked, the form would submit `name="1"`. Pass an explicit value for meaningful submissions — e.g. `<ry-tile-checkbox name="services[]" value="forms">`.
 *
 * @gotcha  Setting `el.checked = true` programmatically does NOT fire `ry:change` — the setter writes directly to the inner input + attribute. To trigger event flow, dispatch `change` on the inner input. Mirrors the same pattern as `<ry-switch>`.
 *
 * @gotcha  `setup()` is idempotent — renderTile bails if `[data-ry-target="tile"]` exists. Authored markup can override.
 *
 * @example basic
 * ```html
 * <ry-tile-checkbox name="features[]" value="forms"
 *                   heading="EOY Forms"
 *                   meta="Generates 1094-C / 1095-C."></ry-tile-checkbox>
 * ```
 *
 * @example pre-checked-with-badge
 * ```html
 * <ry-tile-checkbox name="features[]" value="tracking" checked badge="Included"
 *                   heading="Eligibility Tracking"
 *                   meta="Measurement / administrative period tracking."></ry-tile-checkbox>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-tile-checkbox name="features[]" value="enterprise" disabled
 *                   heading="Enterprise"
 *                   meta="Contact sales to enable."></ry-tile-checkbox>
 * ```
 *
 * @example with-warn
 * ```html
 * <ry-tile-checkbox name="opt-ins[]" value="risky" warn
 *                   heading="Experimental feature"
 *                   meta="Subject to breaking changes."></ry-tile-checkbox>
 * ```
 *
 * @tutorial mount-renders-input-and-tile
 * ```js
 * const el = mount('<ry-tile-checkbox name="x" value="a" heading="A"></ry-tile-checkbox>');
 * await expectEqual(!!el.querySelector('input[type="checkbox"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="tile"]'), true);
 * ```
 *
 * @tutorial default-value-is-1
 * ```js
 * // Wrong assumption: value defaults to empty. ry-tile-checkbox uses HTML's
 * // default of "1" so the rendered input has a meaningful submit value.
 * const el = mount('<ry-tile-checkbox name="x" heading="X"></ry-tile-checkbox>');
 * const input = el.querySelector('input');
 * await expectAttr(input, 'value', '1');
 * ```
 *
 * @tutorial user-toggle-fires-ry-change
 * ```js
 * const el = mount('<ry-tile-checkbox name="x" value="a" heading="A"></ry-tile-checkbox>');
 * const input = el.querySelector('input');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail));
 * input.checked = true;
 * input.dispatchEvent(new Event('change', { bubbles: true }));
 * await expectEqual(el.checked, true);
 * await expectAttr(el, 'checked', '');
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].value, 'a');
 * await expectEqual(fired[0].checked, true);
 * ```
 *
 * @tutorial same-name-tiles-do-NOT-coordinate
 * ```js
 * // Wrong assumption: shared [name] means radio-group behavior. NO — tiles
 * // are independent checkboxes; same-name is just for form-array submission.
 * const wrap = mount(`
 *   <div>
 *     <ry-tile-checkbox id="a" name="grp" value="a" checked heading="A"></ry-tile-checkbox>
 *     <ry-tile-checkbox id="b" name="grp" value="b" heading="B"></ry-tile-checkbox>
 *   </div>
 * `);
 * const a = wrap.querySelector('#a');
 * const b = wrap.querySelector('#b');
 * const bInput = b.querySelector('input');
 * bInput.checked = true;
 * bInput.dispatchEvent(new Event('change', { bubbles: true }));
 * await expectEqual(a.checked, true, 'A still checked');
 * await expectEqual(b.checked, true, 'B also checked');
 * ```
 *
 * @tutorial programmatic-checked-setter-is-silent
 * ```js
 * // Wrong assumption: setting checked = true fires ry:change. It does NOT —
 * // it writes the attribute and inner input state but is silent.
 * const el = mount('<ry-tile-checkbox name="x" value="a" heading="A"></ry-tile-checkbox>');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * el.checked = true;
 * await expectEqual(el.checked, true);
 * await expectAttr(el, 'checked', '');
 * await expectEqual(fired.length, 0, 'programmatic setter is silent');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange cancels the inner input's click —
 * // the tile stays where it was and ry:change never fires.
 * const el = mount('<ry-tile-checkbox name="x" value="a" heading="A"></ry-tile-checkbox>');
 * const input = el.querySelector('input');
 * const changed = [];
 * el.addEventListener('ry:change', () => changed.push(1));
 * let seen = null;
 * el.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * input.click();
 * await expectEqual(seen.checked, true, 'detail.checked is the NEXT state');
 * await expectEqual(seen.previous, false, 'detail.previous is the current state');
 * await expectEqual(el.checked, false, 'vetoed click leaves it unchecked');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * input.click();
 * await expectEqual(el.checked, true, 'without the veto, the click toggles');
 * await expectEqual(changed.length, 1, 'ry:change fired once');
 * ```
 *
 * @see ry-tile-radio
 * @see ry-switch
 */

import { RyElement } from '../core/ry-element.js';
import { renderTile } from './ry-tile-shared.js';

export class RyTileCheckbox extends RyElement {
  static observedAttributes = ['checked', 'disabled'] as const;

  #input: HTMLInputElement | null = null;

  setup(): void {
    renderTile(this, 'checkbox');
    this.#input = this.$<HTMLInputElement>('input.ry-tile__input');
    if (this.#input) {
      this.on(this.#input, 'click', this.#handleClick);
      this.on(this.#input, 'change', this.#handleChange);
    }
  }

  // The interception point. A checkbox's `click` pre-activation has ALREADY
  // flipped `checked` by the time the event dispatches, so `input.checked`
  // here is the NEXT value. Cancelling the click makes the platform restore
  // it and no native `change` fires.
  #handleClick = (event: MouseEvent): void => {
    if (!this.#input) return;
    const checked = this.#input.checked;
    if (!this.emit('beforechange', {
      value: this.#input.value,
      checked,
      previous: !checked,
    })) event.preventDefault();
  };

  #handleChange = (): void => {
    if (!this.#input) return;
    const checked = this.#input.checked;
    if (checked) this.setAttribute('checked', '');
    else this.removeAttribute('checked');

    this.emit('change', { value: this.#input.value, checked });
  };

  get checked(): boolean { return this.#input?.checked ?? false; }
  set checked(v: boolean) {
    if (!this.#input) return;
    this.#input.checked = v;
    if (v) this.setAttribute('checked', '');
    else this.removeAttribute('checked');
  }

  get value(): string { return this.#input?.value ?? ''; }
}

customElements.define('ry-tile-checkbox', RyTileCheckbox);
